import { all, call, fork, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { ApiResponse, IApiQueryOptions } from 'api/ApiClient';
import { routeConstants } from 'routes';
import { browserHistory } from 'routes/Router';
import { ApiQueryOperator, IAction, IApiList, ICreatePaymentPayload, IEmployee, IHoldbackFixed, IHoldbackPercent, IPayee, IPaymentSet, IPaymentSetCalculations, ITipCalculatorChangeLogEntry, IUploadPayment, PayoutRefType } from 'types';
import { convertEmployeeToPayee } from 'utils';
import { formatErrorMessages } from 'utils/errors';
import * as API from '../../api';
import * as Actions from './actions';
import * as ActionTypes from './types';

export function* getChangeLog(paymentId: string): Generator<any, ApiResponse<IApiList<ITipCalculatorChangeLogEntry>> | undefined, any> {
  try {
    return yield call(API.Payments.showPaymentChangeLog, paymentId, { limit: 0 });
  } catch (error) {
    return undefined;
  }
}

export function* getPayment({ payload }: IAction<ActionTypes.Payment, { paymentId: string; skipCalcsRequest?: boolean }>) {
  try {
    const response: ApiResponse<IPaymentSet> = yield call(API.Payments.showPayment, payload.paymentId);

    // Get calculation results if the payment has a ref type of `calculator`
    if (payload.skipCalcsRequest !== true && response.body.refType === PayoutRefType.CALCULATOR) {
      const [calcResponse, changeLogResponse]: [ApiResponse<IPaymentSetCalculations>, ApiResponse<IApiList<ITipCalculatorChangeLogEntry>> | undefined] = yield all([
        call(API.Payments.showPaymentTipCalculator, payload.paymentId),
        call(getChangeLog, payload.paymentId),
      ]);

      yield put(Actions.getPaymentSuccess({ ...response.body, calculations: calcResponse.body, changeLog: changeLogResponse?.body?.data }));
    } else {
      yield put(Actions.getPaymentSuccess(response.body));
    }
  } catch (error: any) {
    yield put(Actions.getPaymentError(error));
  }
}

export function* uploadPayment({ payload }: IAction<ActionTypes.Payment, { siteId: string; payments: IUploadPayment[]; onSuccess?(payments: IUploadPayment[]): void }>) {
  try {
    const { siteId, payments, onSuccess } = payload;
    const paymentMatches: IUploadPayment[] = yield all(payments.map((payment) => call(getEmployeeMatch, payment, siteId)));
    if (onSuccess) yield call(onSuccess, paymentMatches);

    yield put(Actions.uploadPaymentSuccess(paymentMatches));
  } catch (error: any) {
    yield put(Actions.uploadPaymentError(error));
  }
}

export function* getEmployeeMatch(payment: IUploadPayment, siteId: string) {
  let response: ApiResponse<IApiList<IEmployee>>;

  if (payment.employeeId) {
    response = yield call(API.Sites.listEmployees, siteId, {
      q: [{ key: 'sites.employee_id', value: payment.employeeId, operator: ApiQueryOperator.eq }],
    });
  }

  // if no employeeId or no match found, get closest matches by name
  if (!payment.employeeId || (payment.name && response.body.data.length === 0)) {
    response = yield call(API.Sites.listEmployees, siteId, {
      q: [{ key: 'name', value: `^${payment.name}`, operator: ApiQueryOperator.like }],
    });
  }

  const employees = response?.body.data || [];
  const employee = employees.length === 1 ? employees[0] : undefined;
  return { ...payment, employeeMatch: employees, employee };
}

interface IGetPayeesPayload {
  siteId: string;
  options?: IApiQueryOptions;
  siteHoldbacks?: IHoldbackFixed | IHoldbackPercent;
  onSuccess?(payees: IPayee[]): void;
}

export function* getPayees({ payload }: IAction<ActionTypes.Payment, IGetPayeesPayload>) {
  try {
    const { siteId, options, siteHoldbacks, onSuccess } = payload;

    const response: ApiResponse<IApiList<IEmployee>> = yield call(API.Sites.listEmployees, siteId, options);
    const payees = response.body.data.map((emp) => convertEmployeeToPayee(emp, siteId, siteHoldbacks));

    if (onSuccess) yield call(onSuccess, payees);

    yield put(Actions.getPayeesSuccess(payees));
  } catch (error: any) {
    yield put(Actions.getPayeesError(error));
  }
}

export function* payPaymentSet(action: IAction<ActionTypes.Payment, ICreatePaymentPayload>) {
  try {
    const response: ApiResponse<IPaymentSet> = yield call(API.Payments.createPayment, action.payload);
    yield call(browserHistory.replace, `${routeConstants.private.makePaymentSummary}?paymentId=${response.body.id}`);
    yield put(Actions.payPaymentSetSuccess(response.body));
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    yield put(Actions.payPaymentSetError(formattedError));
  }
}

export function* watchGetPayment() {
  yield takeEvery(ActionTypes.Payment.GET_PAYMENT_REQUEST, getPayment);
}

export function* watchUploadPayment() {
  yield takeLatest(ActionTypes.Payment.UPLOAD_PAYMENT_REQUEST, uploadPayment);
}

export function* watchGetPayees() {
  yield takeLatest(ActionTypes.Payment.GET_PAYEES_REQUEST, getPayees);
}

export function* watchPayPaymentSet() {
  yield takeLatest(ActionTypes.Payment.CREATE_PAYMENT_REQUEST, payPaymentSet);
}

function* paymentSaga() {
  yield all([fork(watchGetPayment), fork(watchUploadPayment), fork(watchGetPayees), fork(watchPayPaymentSet)]);
}

export { paymentSaga };
